import { datadogLogs } from '@datadog/browser-logs';
import { signInWithRedirect } from 'aws-amplify/auth';

import { signOutAndReauthenticate } from 'src/common/external/aws/signOut';

export const lastReauthStorageKey = 'last-reauth-attempt-at';
export const REAUTH_COOLDOWN_MS = 60_000;

// Returning to the login form only helps a session that has really gone stale.
// One the backend keeps refusing takes that form again the moment the user
// finishes with it, so a repeat this soon is that loop rather than a second
// stale session. The loop runs in one tab, and sessionStorage both survives the
// trip out to Cognito and leaves another tab recovering at the same time its
// own first attempt.
function claimReauthAttempt() {
  try {
    const last = Number(sessionStorage.getItem(lastReauthStorageKey));
    if (last && Date.now() - last < REAUTH_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(lastReauthStorageKey, String(Date.now()));
    return true;
  } catch {
    // Storage can throw (disabled/quota). With no record to read back there is
    // no way to tell a loop from a first attempt, so stop here.
    return false;
  }
}

// Route through Cognito's Hosted UI via Amplify's OAuth flow rather than a
// hand-built /login URL: v6 validates a `state` param on the return trip that
// only exists if Amplify itself initiated the redirect.
//
// Resolves false when the browser is staying put, which is the caller's cue to
// show a terminal notice. Never rejects, because a fire-and-forget rejection
// leaves the app rendering nothing.
export async function redirectUnauthedUser(): Promise<boolean> {
  try {
    await signInWithRedirect();
    return true;
  } catch (err) {
    const errorName = (err as { name?: string } | undefined)?.name;
    // v6 throws UserAlreadyAuthenticated while it still holds a session — i.e.
    // /profile failed for a reason re-login can't fix (backend rejecting a
    // still-valid token, clock skew). Retrying would silently re-auth off the
    // live Hosted-UI cookie and loop, so clear that cookie and come back to the
    // login form.
    if (errorName === 'UserAlreadyAuthenticatedException') {
      datadogLogs.logger.warn('auth.redirect.stale_session_signout');
      if (!claimReauthAttempt()) {
        return false;
      }
      try {
        await signOutAndReauthenticate();
        return true;
      } catch (signOutErr) {
        datadogLogs.logger.error(
          'auth.redirect.signout_failed',
          {},
          signOutErr instanceof Error ? signOutErr : undefined
        );
        return false;
      }
    }
    datadogLogs.logger.error(
      'auth.redirect.failed',
      { auth: { errorName } },
      err instanceof Error ? err : undefined
    );
    return false;
  }
}
